// Feedback & Reports — shared config for the reusable feedback modal.
//
// One modal serves three intents (bug / feature / feedback); the intent presets
// the type + copy, and the type dropdown stays switchable across all five types.

import type { IconSvgElement } from '@/components/ui/icon';
import { Bug01Icon, Message01Icon, SparklesIcon } from '@/components/ui/icon/glyphs';

/** Public Canny feature-request board (also linked from the sidebar/footer). */
export const CANNY_FEATURE_BOARD_URL = 'https://morfi.canny.io/feature-requests';

/** Message length cap — mirrors the backend `FeedbackRequestSchema` (1000). */
export const FEEDBACK_MESSAGE_MAX = 1000;

/** The five selectable feedback types (wire enum — must match the backend). */
export type FeedbackType = 'bug' | 'feature' | 'feedback' | 'confusing' | 'other';

/** Call-site intents that preset the modal's type + copy. */
export type FeedbackIntent = 'bug' | 'feature' | 'feedback';

/** Which call site opened the modal (telemetry). */
export type FeedbackSource =
  | 'wallet_dropdown'
  | 'sidebar'
  | 'header_menu'
  | 'footer'
  | 'settings'
  | 'rewards_menu'
  | 'error_boundary'
  | 'error_toast'
  | 'empty_state';

/**
 * Optional context attached to the Slack message for triage. Identity is never
 * included here — the backend reads the acting pubkey from the session.
 */
export interface FeedbackContext {
  surface?: string | undefined;
  route?: string | undefined;
  network?: string | undefined;
  locale?: string | undefined;
  appVersion?: string | undefined;
  errorDigest?: string | undefined;
  errorCode?: string | undefined;
}

export interface FeedbackTypeOption {
  value: FeedbackType;
  emoji: string;
}

/** Type dropdown options. Labels/descriptions come from i18n (`feedback.types.*`). */
export const FEEDBACK_TYPE_OPTIONS: readonly FeedbackTypeOption[] = [
  { value: 'bug', emoji: '🐞' },
  { value: 'feature', emoji: '✨' },
  { value: 'feedback', emoji: '💡' },
  { value: 'confusing', emoji: '😕' },
  { value: 'other', emoji: '💬' },
];

export interface FeedbackIntentPreset {
  /** Preselected type when opened with this intent. */
  type: FeedbackType;
  /** Header glyph. */
  icon: IconSvgElement;
}

/** Intent → preset. Copy (title/description/cta/placeholder) keyed by intent in i18n. */
export const FEEDBACK_INTENT_PRESETS: Record<FeedbackIntent, FeedbackIntentPreset> = {
  bug: { type: 'bug', icon: Bug01Icon },
  feature: { type: 'feature', icon: SparklesIcon },
  feedback: { type: 'feedback', icon: Message01Icon },
};
